import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, GitBranch, KeyRound, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useActiveTenants } from '@/shared/hooks/useTenantsSearch.ts';
import { TenantMultiSelect } from '@/shared/components/TenantMultiSelect.tsx';
import { ConfirmModal } from '@/shared/components/ConfirmModal.tsx';
import { RowActionMenu } from '@/shared/components/RowActionMenu.tsx';
import {
  useDeploySourceList,
  useCreateDeploySource,
  useUpdateDeploySource,
  useRotateDeploySourceSecret,
  useDeleteDeploySource,
} from '../hooks/useDeploySources.ts';
import { WebhookSecretModal } from './WebhookSecretModal.tsx';
import type { DeploySourceRow } from '@/shared/types/deploy.ts';

/* ── schema ───────────────────────────────────────────── */

const formSchema = z.object({
  workspace: z
    .string()
    .min(1, 'Workspace é obrigatório')
    .regex(/^[a-zA-Z0-9._-]+$/, 'Use apenas letras, números, ponto, hífen e underscore'),
  repoSlug: z
    .string()
    .min(1, 'Repositório é obrigatório')
    .regex(/^[a-zA-Z0-9._-]+$/, 'Use apenas letras, números, ponto, hífen e underscore'),
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  branches: z.string().min(1, 'Informe ao menos uma branch'),
  margemMinutos: z.coerce.number().int().min(0).max(240),
});

type FormFields = z.infer<typeof formSchema>;

type ViewMode = 'list' | 'form';

/* ── página ───────────────────────────────────────────── */

export function DeploySourceListPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editing, setEditing] = useState<DeploySourceRow | null>(null);
  const [deleting, setDeleting] = useState<DeploySourceRow | null>(null);
  const [rotating, setRotating] = useState<DeploySourceRow | null>(null);
  const [novoSegredo, setNovoSegredo] = useState<{ id: string; secret: string } | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [tenantError, setTenantError] = useState('');

  const { data: activeTenants } = useActiveTenants();
  const { data, isLoading } = useDeploySourceList({ page: 1, limit: 100 });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(formSchema),
    defaultValues: { workspace: '', repoSlug: '', nome: '', branches: 'main,master', margemMinutos: 10 },
  });

  useEffect(() => {
    if (viewMode !== 'form') return;

    if (editing) {
      reset({
        workspace: editing.workspace,
        repoSlug: editing.repoSlug,
        nome: editing.nome,
        branches: editing.branches,
        margemMinutos: editing.margemMinutos,
      });
      setSelectedIds(editing.tenants.map((t) => t.tenantId));
    } else {
      reset({ workspace: '', repoSlug: '', nome: '', branches: 'main,master', margemMinutos: 10 });
      setSelectedIds([]);
    }
    setTenantError('');
  }, [viewMode, editing, reset]);

  function backToList() {
    setViewMode('list');
    setEditing(null);
  }

  const createMutation = useCreateDeploySource((secret, id) => {
    backToList();
    setNovoSegredo({ id, secret });
  });
  const updateMutation = useUpdateDeploySource(backToList);
  const rotateMutation = useRotateDeploySourceSecret((secret, id) => {
    setRotating(null);
    setNovoSegredo({ id, secret });
  });
  const deleteMutation = useDeleteDeploySource(() => setDeleting(null));

  function handleToggle(id: string) {
    setTenantError('');
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function onSubmit(values: FormFields) {
    if (selectedIds.length === 0) {
      setTenantError('Selecione os tenants afetados por deploys deste repositório');
      return;
    }

    if (editing) {
      updateMutation.mutate({
        id: editing.id,
        input: {
          nome: values.nome,
          branches: values.branches,
          margemMinutos: values.margemMinutos,
          tenantIds: selectedIds,
        },
      });
      return;
    }

    createMutation.mutate({ ...values, tenantIds: selectedIds });
  }

  /* ── formulário ─────────────────────────────────────── */

  if (viewMode === 'form') {
    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    return (
      <div>
        <button
          onClick={backToList}
          className="mb-4 flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Voltar
        </button>

        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h1 className="mb-5 text-xl font-semibold">
            {editing ? 'Editar Repositório' : 'Novo Repositório'}
          </h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Workspace</label>
                <input
                  {...register('workspace')}
                  disabled={!!editing}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800"
                  placeholder="teamnovogestao"
                />
                {errors.workspace && (
                  <p className="mt-1 text-xs text-red-500">{errors.workspace.message}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Repositório</label>
                <input
                  {...register('repoSlug')}
                  disabled={!!editing}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800"
                  placeholder="woli.uptime.monitor"
                />
                {errors.repoSlug && (
                  <p className="mt-1 text-xs text-red-500">{errors.repoSlug.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Nome</label>
              <input
                {...register('nome')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Como este sistema aparece no painel"
              />
              {errors.nome && <p className="mt-1 text-xs text-red-500">{errors.nome.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Branches <span className="text-gray-400">(separadas por vírgula)</span>
                </label>
                <input
                  {...register('branches')}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  placeholder="main,master"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Pipelines em outras branches são ignorados.
                </p>
                {errors.branches && (
                  <p className="mt-1 text-xs text-red-500">{errors.branches.message}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Margem após o deploy (min)</label>
                <input
                  type="number"
                  {...register('margemMinutos')}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Somada ao fim do pipeline para cobrir o warm-up da aplicação.
                </p>
                {errors.margemMinutos && (
                  <p className="mt-1 text-xs text-red-500">{errors.margemMinutos.message}</p>
                )}
              </div>
            </div>

            <TenantMultiSelect
              tenants={activeTenants ?? []}
              selected={selectedIds}
              selectAll={false}
              onToggle={handleToggle}
              onSelectAll={() => {
                setTenantError('');
                setSelectedIds((activeTenants ?? []).map((t) => t.id));
              }}
              onClearAll={() => setSelectedIds([])}
            />
            {tenantError && <p className="text-xs text-red-500">{tenantError}</p>}

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <button
                type="button"
                onClick={backToList}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando...' : editing ? 'Salvar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  /* ── listagem ───────────────────────────────────────── */

  const sources = data?.data ?? [];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Repositórios de Deploy</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Cada pipeline bem-sucedido nestes repositórios gera um deploy pendente de revisão.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setViewMode('form');
          }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} /> Novo Repositório
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : sources.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <GitBranch size={28} />
            <p className="text-sm">Nenhum repositório cadastrado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 text-left text-xs uppercase text-gray-500 dark:border-gray-800">
              <tr>
                <th className="px-4 py-3 font-medium">Ações</th>
                <th className="px-4 py-3 font-medium">Repositório</th>
                <th className="px-4 py-3 font-medium">Branches</th>
                <th className="px-4 py-3 font-medium">Margem</th>
                <th className="px-4 py-3 font-medium">Tenants</th>
                <th className="px-4 py-3 font-medium">Pendentes</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr
                  key={source.id}
                  className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                >
                  <td className="px-4 py-3">
                    <RowActionMenu
                      actions={[
                        {
                          label: 'Editar',
                          icon: <Pencil size={14} />,
                          onClick: () => {
                            setEditing(source);
                            setViewMode('form');
                          },
                        },
                        {
                          label: 'Novo segredo',
                          icon: <KeyRound size={14} />,
                          onClick: () => setRotating(source),
                        },
                        {
                          label: 'Remover',
                          icon: <Trash2 size={14} />,
                          variant: 'danger',
                          onClick: () => setDeleting(source),
                        },
                      ]}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{source.nome}</div>
                    <div className="font-mono text-xs text-gray-500">
                      {source.workspace}/{source.repoSlug}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{source.branches}</td>
                  <td className="px-4 py-3">{source.margemMinutos} min</td>
                  <td className="px-4 py-3">
                    {source.tenants.length === 0 ? (
                      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        sem mapeamento
                      </span>
                    ) : (
                      `${source.tenants.length} tenant(s)`
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {source.draftsPendentes > 0 ? (
                      <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {source.draftsPendentes}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {deleting && (
        <ConfirmModal
          title="Remover repositório"
          message={`Deploys de ${deleting.workspace}/${deleting.repoSlug} deixarão de gerar rascunhos. As manutenções já aprovadas permanecem.`}
          confirmLabel="Remover"
          isLoading={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => deleteMutation.mutate(deleting.id)}
        />
      )}

      {rotating && (
        <ConfirmModal
          title="Gerar novo segredo"
          message="O segredo atual deixa de valer imediatamente e o webhook no Bitbucket precisa ser atualizado — até lá, deploys deste repositório não serão registrados."
          confirmLabel="Gerar novo"
          isLoading={rotateMutation.isPending}
          onCancel={() => setRotating(null)}
          onConfirm={() => rotateMutation.mutate(rotating.id)}
        />
      )}

      {novoSegredo && (
        <WebhookSecretModal
          sourceId={novoSegredo.id}
          secret={novoSegredo.secret}
          onClose={() => setNovoSegredo(null)}
        />
      )}
    </div>
  );
}
